import React, { useRef, useState, useEffect, startTransition } from "react";
import { useNavigate } from "react-router-dom";

// --- Same Responsive Hook from Navbar ---
const useResponsive = () => {
  const [screenWidth, setScreenWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return {
    screenWidth,
    isMobile: screenWidth < 640,
    isTablet: screenWidth >= 640 && screenWidth < 1024,
    isDesktop: screenWidth >= 1024,
    isSmall: screenWidth < 375
  };
};

export default function RadioPlayer({
  streamUrl = "https://listen.radioking.com/radio/736103/stream/802454"
}) {
  const navigate = useNavigate();
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState("");
  const [volume, setVolume] = useState(1);

  const { isMobile, isTablet, isDesktop } = useResponsive();

  useEffect(() => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    audioRef.current.load();
    startTransition(() => {
      setPlaying(false);
      setError("");
      setVolume(1);
    });
  }, [streamUrl]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current
        .play()
        .then(() => setPlaying(true))
        .catch(() => setError("Playback failed"));
    }
  };

  const handleAudioError = () => {
    setError("Stream unavailable");
    setPlaying(false);
  };

  const handleComeInside = () => {
    navigate("/main");
  };

  // --- Styles that adapt by screen size ---
  const containerStyle = {
    backgroundColor: "#1a0000",
    minHeight: "100vh",
    color: "#fff",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: isMobile ? 16 : isTablet ? 24 : 32,
    fontFamily: "'Arial Black', sans-serif",
    textAlign: "center"
  };

  const headingStyle = {
    fontSize: isMobile ? "24px" : isTablet ? "28px" : "32px",
    margin: 0,
    color: "#ffffff",
    fontWeight: "bold"
  };

  const subheadingStyle = {
    fontSize: isMobile ? "16px" : isTablet ? "20px" : "22px",
    marginTop: 8,
    color: "#bdbdbd",
    fontWeight: "500"
  };

  const playerBoxStyle = {
    marginTop: isMobile ? 24 : 36,
    backgroundColor: "#ffffff",
    padding: isMobile ? "12px 14px" : "16px 20px",
    borderRadius: 12,
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    alignItems: "center",
    gap: isMobile ? 10 : 14,
    width: "100%",
    maxWidth: 400
  };

  const playButtonStyle = {
    backgroundColor: "#000",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    padding: isMobile ? "6px 12px" : "8px 16px",
    fontWeight: "bold",
    fontSize: isMobile ? 14 : 16,
    cursor: "pointer",
    width: isMobile ? "100%" : "auto"
  };

  const streamTextStyle = {
    color: "#D60000",
    fontWeight: "600",
    fontSize: isMobile ? 12 : 14,
    flex: 1,
    textAlign: isMobile ? "center" : "left"
  };

  const volumeStyle = {
    width: isMobile ? "100%" : 80,
    accentColor: "#00e5ff",
    cursor: "pointer"
  };

  const comeInsideButtonStyle = {
    marginTop: 30,
    backgroundColor: "#fff",
    color: "#00aaff",
    border: "2px solid #00aaff",
    borderRadius: 24,
    padding: isMobile ? "6px 16px" : "8px 20px",
    fontWeight: "bold",
    fontSize: isMobile ? 12 : 14,
    letterSpacing: 1,
    cursor: "pointer"
  };

  return (
    <div style={containerStyle}>
      {/* Headings */}
      <h1 style={headingStyle}>Welcome to HOTMESS.</h1>
      <p style={subheadingStyle}>Stream live. Vibe wild.</p>

      {/* Player Box */}
      <div style={playerBoxStyle}>
        <button onClick={handlePlayPause} style={playButtonStyle}>
          {playing ? "Pause" : "Play"}
        </button>

        <span style={streamTextStyle}>
          {error || "Live Radio Stream"}
        </span>

        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={volume}
          onChange={(e) => setVolume(Number(e.target.value))}
          style={volumeStyle}
          aria-label="Volume"
        />

        <audio
          ref={audioRef}
          src={streamUrl}
          preload="none"
          onError={handleAudioError}
          style={{ display: "none" }}
        />
      </div>

      {/* COME INSIDE Button */}
      <button onClick={handleComeInside} style={comeInsideButtonStyle}>
        COME INSIDE
      </button>
    </div>
  );
}
